// scanQuota (frontend) — UX pre-check that mirrors the authoritative counter in
// functions/oracleFunctions/scanQuota.js. This only decides what the capture
// screen SAYS before calling the backend; runOraclePipeline re-enforces the
// limit server-side, so this can never be the sole guard.
//
// ── THE DAILY LIMIT IS OFF ──────────────────────────────────────────────────
//
// Both allowances are Infinity, so `isScanBlocked` is always false and the
// capture screen never parks in `quotaBlocked`. Read the backend file for why
// the counter survives the cap and what it costs. `hoursUntilQuotaReset` is
// kept for the same reason as the constants: it is the copy that comes back if
// a ceiling does.
//
// Kept as a hand-mirrored pair rather than a shared module for the usual
// reason: CRA cannot import from outside src/, and Cloud Functions cannot
// import from src/. Any change to the allowances or the legacy fallback MUST
// update both copies in the same commit.
//
// See the backend file for why this is a counter rather than the old
// `last_free_scan_date` + `scan_unlock_credits` pair, and for the legacy-doc
// fallback the two share.

// The unlock quota is NOT one of the mirrored constants above — it lives in the
// scanFeedbackApi contract pair, which is already the single source for it on both
// sides. Importing beats re-declaring a second 10.
import { UNLOCK_FEEDBACK_QUOTA } from '../contracts/scanFeedbackApi';

export const FREE_DAILY_SCANS = Infinity;
export const PRO_DAILY_SCANS = Infinity;

export function todayUtc(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

export function dailyAllowance(isPro) {
  return isPro ? PRO_DAILY_SCANS : FREE_DAILY_SCANS;
}

// Scans already spent today. Falls back for pre-counter docs: no `scan_day` but
// `last_free_scan_date === today` means one scan was used before the deploy.
export function scansUsedToday(userData, today = todayUtc()) {
  const u = userData || {};

  if (u.scan_day === today) {
    const n = Number(u.scan_day_count);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }
  if (u.scan_day == null && u.last_free_scan_date === today) return 1;
  return 0;
}

// Whole picture, so a caller can render "2 OF 5 TODAY" without recomputing.
export function computeQuotaState(userData, { isPro = false, today = todayUtc() } = {}) {
  const allowance = dailyAllowance(isPro);
  const used = scansUsedToday(userData, today);
  return {
    used,
    allowance,
    remaining: Math.max(0, allowance - used),
    blocked: used >= allowance,
  };
}

export function isScanBlocked(userData, { isPro = false, today = todayUtc() } = {}) {
  return computeQuotaState(userData, { isPro, today }).blocked;
}

// ── One reading outstanding at a time ───────────────────────────────────────
//
// MIRROR of hasOutstandingUnlock in functions/oracleFunctions/scanUnlock.js —
// NOT of the scanQuota pair above, despite living here. It sits in this file
// because this is where Create.js already looks for "may I scan?", and one
// import beats a second module for a single predicate. Any change to the
// arithmetic MUST update both copies in the same commit; read the backend
// header for WHY the high-water mark alone answers the question.
//
// Client-side this is the AFFORDANCE only: it stops us base64-encoding a
// multi-megabyte portrait and shipping it to a server that will refuse. The
// guarantee is runOraclePipeline step 2.6, which re-checks against a user doc
// the client cannot forge. Deleting the server copy would be a vulnerability;
// deleting this one only wastes the user's upload.
//
// Note this deliberately does NOT check the unlock-gate flag. The client has no
// view of UNLOCK_GATE_ENABLED, so with the gate off `unlock_debt_target` is
// simply never written and the predicate is false for everyone — the flag
// gates the field, and the field gates this.
export function hasOutstandingUnlock(userData, { isPro = false } = {}) {
  if (isPro) return false;
  const mark = Number(userData && userData.unlock_debt_target);
  if (!Number.isFinite(mark) || mark <= 0) return false;
  return judgmentsGiven(userData) < mark;
}

function judgmentsGiven(userData) {
  const n = Number(userData && userData.scan_feedback_total);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

// `{ done, required }` toward the outstanding scan, for the client-inferred
// refusal — the server sends this block itself when IT refuses, so this only
// covers the pre-check path where no request was made.
//
// MIRROR of progressToward in functions/oracleFunctions/scanUnlock.js. Reports
// progress toward the target, NOT the raw lifetime counter: a user on their
// second scan has ten readings banked that predate it, and the raw number
// renders "13 / 10" in a ten-cell row.
export function localUnlockProgress(userData) {
  const mark = Number(userData && userData.unlock_debt_target);
  const remaining = Math.max(0, (Number.isFinite(mark) ? mark : 0) - judgmentsGiven(userData));
  return {
    state: 'locked',
    done: Math.max(0, UNLOCK_FEEDBACK_QUOTA - remaining),
    required: UNLOCK_FEEDBACK_QUOTA,
    paths: ['trial', 'judging'],
  };
}

// Hours until the daily allowance resets (UTC midnight), rounded up, minimum 1.
//
// The quota exists to bound cost, but framed as "your next reading opens in 6h"
// it becomes a reason to come back instead of a wall — which is the whole point
// of the returning-user surface. Rounded up and floored at 1 so it never reads
// "in 0 hours".
export function hoursUntilQuotaReset(now = new Date()) {
  const nextUtcMidnight = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
  );
  return Math.max(1, Math.ceil((nextUtcMidnight - now.getTime()) / 3600000));
}
